import { useEffect, useState } from 'react';
import type { MessageDetail } from 'zitimail-shared-types';

import { mail } from '../api/mail';
import { useToast } from '../components/Toasts';
import { size } from './format';

/** More than this is not drawn on screen; the .eml download has it all. */
const SHOWN = 1_000_000;

/** A file name from a subject, safe on every desktop. */
export function emlName(subject: string): string {
  const name = [...subject]
    .map((c) => (c < ' ' || '\\/:*?"<>|'.includes(c) ? '_' : c))
    .join('')
    .trim()
    .slice(0, 80);
  return `${name || 'message'}.eml`;
}

/**
 * A message's source: every header and part exactly as the server holds
 * it, to work out why mail was filed, failed a check or looks wrong. Shown
 * as text, never rendered.
 */
export function SourceDialog({
  detail,
  onClose,
}: {
  detail: MessageDetail;
  onClose: () => void;
}): JSX.Element {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  useEffect(() => {
    let live = true;
    mail
      .source(detail.folder, detail.uid)
      .then((t) => live && setText(t))
      .catch((e: unknown) => {
        if (live) {
          setError(e instanceof Error ? e.message : 'The source could not be read.');
        }
      });
    return () => {
      live = false;
    };
  }, [detail.folder, detail.uid]);
  const copy = () => {
    if (text === null) {
      return;
    }
    navigator.clipboard.writeText(text).then(
      () => toast('Source copied.'),
      () => toast('Could not copy. Select the text, or download the .eml.', { tone: 'error' }),
    );
  };
  return (
    <div className="modal-scrim" role="presentation" onClick={onClose}>
      <div
        className="source-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Message source"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        // The mail shortcuts (e archives, # deletes) must not act behind it.
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Escape') {
            onClose();
          }
        }}
      >
        <header className="source-head">
          <div className="source-title">
            <h3>Message source</h3>
            <p>
              {detail.subject || '(no subject)'}
              <span className="source-size">{size(detail.sizeBytes)}</span>
            </p>
          </div>
          <span className="source-actions">
            <button type="button" className="btn ghost" onClick={copy} disabled={text === null}>
              Copy
            </button>
            <a
              className="btn ghost"
              href={mail.sourceUrl(detail.folder, detail.uid)}
              download={emlName(detail.subject)}
            >
              Download .eml
            </a>
            <button type="button" className="btn" onClick={onClose} autoFocus>
              Close
            </button>
          </span>
        </header>
        {error ? (
          <p className="source-status" role="alert">
            {error}
          </p>
        ) : text === null ? (
          <p className="source-status">Loading the source…</p>
        ) : (
          <>
            <pre className="source-text" tabIndex={0} aria-label="Source">
              {text.length > SHOWN ? text.slice(0, SHOWN) : text}
            </pre>
            {text.length > SHOWN && (
              <p className="source-status">
                The first 1 MB is shown. Download the .eml for the whole message.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
